// MP-OS-189 — real URLs for the main views. A tiny History-API router instead of a library:
// the app has eight views and one parameter, and App already owns all view state.
//
// The URL only names WHICH view is open. It never carries scope: the supplier a view shows is
// the signed-in session's (TENANT, locked server-side for a supplier seat), and any query
// parameter is dropped on normalisation, so `?supplier=…` or `?manufacturer=…` can't widen or
// change what is shown. Role gates stay in App (and the API enforces them regardless).

import { useCallback, useEffect, useState } from 'react'
import type { SectionId } from './sections'

export interface Route {
  section: SectionId
  boardId: string | null // only in 'mina': null = the chat, else that dashboard
}

const SECTION_PATHS: Record<SectionId, string> = {
  oversikt: '/overview',
  trends: '/sales-trends',
  forsaljning: '/top-lists',
  marknad: '/packages',
  business: '/business-metrics',
  admin: '/users',
  mina: '/chat',
}

export const DEFAULT_ROUTE: Route = { section: 'oversikt', boardId: null }

// Saved dashboard ids are client uuids; anything else in that slot is not a board link.
const BOARD_ID = /^[A-Za-z0-9_-]{1,64}$/

// `chat` = chatAvailable(…) for this session. "My dashboards" without a board is the chat for
// those who have it (/chat) and the board list for everyone else (/dashboards). MP-OS-193: a
// supplier or an admin's supplier preview never gets /chat as a URL, so a link, Back/Forward or
// a remembered path lands on My dashboards instead.
export function pathFor(route: Route, chat = true): string {
  if (route.section === 'mina' && route.boardId) return `/dashboards/${encodeURIComponent(route.boardId)}`
  if (route.section === 'mina' && !chat) return '/dashboards'
  return SECTION_PATHS[route.section]
}

// Path → route. `null` = not a page of this app (the caller normalises to the default view).
// `/` is an alias, and `/chat` and `/dashboards` both mean "My dashboards" with no board open;
// the caller replaces them with the canonical path for this session.
export function parsePath(pathname: string): Route | null {
  const path = pathname.replace(/\/+$/, '') || '/'
  if (path === '/') return DEFAULT_ROUTE
  if (path === '/dashboards') return { section: 'mina', boardId: null }
  const board = /^\/dashboards\/([^/]+)$/.exec(path)
  if (board) {
    let id: string
    try { id = decodeURIComponent(board[1]) } catch { return null }
    return BOARD_ID.test(id) ? { section: 'mina', boardId: id } : null
  }
  const section = (Object.keys(SECTION_PATHS) as SectionId[]).find((s) => SECTION_PATHS[s] === path)
  return section ? { section, boardId: null } : null
}

const currentPath = () => window.location.pathname + window.location.search + window.location.hash

export function useRoute(chat: boolean) {
  const [route, setRoute] = useState<Route>(() => parsePath(window.location.pathname) ?? DEFAULT_ROUTE)

  // Back/Forward: the browser has already moved; read the route back from the URL.
  useEffect(() => {
    const onPop = () => setRoute(parsePath(window.location.pathname) ?? DEFAULT_ROUTE)
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  // push for a user's navigation, replace for normalising state (alias, unknown or denied path).
  // Navigating to the page you are on adds no history entry.
  const navigate = useCallback((next: Route, options?: { replace?: boolean }) => {
    const path = pathFor(next, chat)
    if (options?.replace) {
      if (currentPath() !== path) window.history.replaceState(null, '', path)
    } else if (currentPath() !== path) {
      window.history.pushState(null, '', path)
    }
    setRoute(next)
  }, [chat])

  return { route, navigate }
}

// Deep links across sign-in. Password sign-in reloads on the same URL, so the link survives on
// its own; Google SSO returns to the site root, so the requested page is parked here first.
// sessionStorage = this tab only, and only a path this app knows (never an external URL).
//
// MP-OS-189 follow-up: the parked path is tied to the account that was signed in when it was
// parked (or `null` for a deep link that never had a session), not just the tab — otherwise
// account B signing in after account A's session expired lands on A's last page.
const RETURN_KEY = 'mp_return_to'

interface ParkedReturn { path: string; owner: string | null }

function isParkedReturn(value: unknown): value is ParkedReturn {
  return !!value && typeof value === 'object'
    && typeof (value as ParkedReturn).path === 'string'
    && ((value as ParkedReturn).owner === null || typeof (value as ParkedReturn).owner === 'string')
}

export function rememberReturnPath(owner: string | null): void {
  if (typeof sessionStorage === 'undefined') return
  const path = window.location.pathname
  // `/admin…` is the staff sign-in entry itself, not a page to come back to.
  if (path.toLowerCase().startsWith('/admin') || !parsePath(path) || path.replace(/\/+$/, '') === '') return
  // MP-OS-189 follow-up: a reload while signed out loses lastUsernameRef (owner becomes null
  // here), which must never overwrite an already-parked owned value — otherwise a different
  // account signing in afterwards would inherit account A's page.
  if (owner === null) {
    const existing = sessionStorage.getItem(RETURN_KEY)
    if (existing) {
      try {
        const raw: unknown = JSON.parse(existing)
        if (isParkedReturn(raw) && raw.owner !== null) return
      } catch { /* malformed or old-format value — fine to replace below */ }
    }
  }
  const parked: ParkedReturn = { path: pathFor(parsePath(path)!), owner }
  sessionStorage.setItem(RETURN_KEY, JSON.stringify(parked))
}

// Read once after sign-in. Returns a canonical in-app path, or null — including when the parked
// path belongs to a different account than the one now signing in, or the stored value predates
// this owner-tagged format (a bare path string, not JSON).
export function takeReturnPath(signedInUsername: string | null): string | null {
  if (typeof sessionStorage === 'undefined') return null
  const stored = sessionStorage.getItem(RETURN_KEY)
  sessionStorage.removeItem(RETURN_KEY)
  if (!stored) return null
  let parked: ParkedReturn | null = null
  try {
    const raw: unknown = JSON.parse(stored)
    if (isParkedReturn(raw)) parked = raw
  } catch { /* malformed or old-format value — ignored below */ }
  if (!parked || (parked.owner !== null && parked.owner !== signedInUsername)) return null
  const route = parked.path.startsWith('/') && !parked.path.startsWith('//') ? parsePath(parked.path) : null
  return route ? pathFor(route) : null
}

export function clearReturnPath(): void {
  if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(RETURN_KEY)
}
